import { useState } from "react";
import { api, useMe } from "./lib";

// Set up two-step sign-in: scan the code with an authenticator app, type the 6 digits it shows, save the recovery codes.
export function TwoStepSetup({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const { toast } = useMe();
  const [setup, setSetup] = useState<{ secret: string; uri: string; qr_svg: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (codes)
    return (
      <div className="stack">
        <strong>Two-step sign-in is on.</strong>
        <span className="muted small">Save these recovery codes somewhere safe, like your password manager. Each one works once if you ever lose your phone. You won't see them again.</span>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 15, background: "var(--panel-2, #f6f4f8)", padding: 12, borderRadius: 10 }}>
          {codes.map((c) => <span key={c}>{c}</span>)}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={() => navigator.clipboard.writeText(codes.join("\n")).then(() => toast("Copied"), () => toast("Couldn't copy", true))}>Copy codes</button>
          <button className="btn primary" onClick={onDone}>I saved them</button>
        </div>
      </div>
    );

  if (!setup)
    return (
      <div className="stack">
        <span className="small">You'll need an authenticator app on your phone, like 1Password, Google Authenticator or Apple Passwords. After your password, the portal will ask for the 6-digit code the app shows.</span>
        {err && <div className="banner bad">{err}</div>}
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                setSetup(await api("/api/me/2fa/start", { method: "POST" }));
              } catch (e: any) {
                setErr(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Starting…" : "Set it up"}
          </button>
          {onCancel && <button className="btn ghost" onClick={onCancel}>Not now</button>}
        </div>
      </div>
    );

  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr(null);
        try {
          const r = await api("/api/me/2fa/confirm", { method: "POST", body: { code } });
          setCodes(r.recovery_codes);
        } catch (e: any) {
          setErr(e.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <span className="small">1. In your authenticator app, add an account and scan this code.</span>
      {/* The SVG is generated by the portal's own server from the setup link; no outside content. */}
      <div style={{ width: 180, height: 180, background: "#fff", padding: 8, borderRadius: 12, border: "1px solid var(--line)" }} dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />
      <span className="muted tiny">On your phone already? <a href={setup.uri}>Open in your authenticator app</a>. Or type this key: <code>{setup.secret}</code></span>
      <label className="field">
        2. Type the 6-digit code it shows
        <input className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} style={{ maxWidth: 180, letterSpacing: ".2em", fontSize: 20 }} autoFocus required />
      </label>
      {err && <div className="banner bad">{err}</div>}
      <div><button className="btn primary" disabled={busy || code.replace(/\D/g, "").length !== 6}>{busy ? "Checking…" : "Turn it on"}</button></div>
    </form>
  );
}
